// LeadPolicyEditorForm.jsx – dynamic offerings per Programme Point
// UI: Ant Design, React 18

import React, { useState, useEffect } from "react";
import {
  Modal, Form, Input, InputNumber, Switch, Button, Select, DatePicker, Row, Col,
  Card, Space, Popconfirm, message, Typography,
} from "antd";
import { UpOutlined, DownOutlined, DeleteOutlined, PlusOutlined, CopyOutlined } from "@ant-design/icons";
import axios from "../../config/axiosInstance";
import moment from "moment";
import useLeadPolicyEditorData from "./useLeadPolicyEditorData";

const { Option } = Select;

const formatDateTime = (val) => (val ? moment(val.valueOf ? val.valueOf() : val).format("DD.MM.YYYY HH:mm") : undefined);
const xmlEscape = (str) =>
    String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const RULE_TEMPLATE = {
  type: "allow",
  badgeType: null,
  participantTypeID: null,
  participantStateID: null,
  startTime: null,
  endTime: null,
  demandsBooking: false,
  demandsPaidBooking: false,
  programmePointBookingID: null,
  programmeOfferingBookingID: null,
  paidProgrammePointBookingID: null,
  paidProgrammeOfferingBookingID: null,
  workGroupID: null,
  maxAllow: null,
};

const LeadPolicyEditorForm = ({ visible, onClose, fetchLeadPolicies, initialData, isEditing, adminMode=false }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEditingFlag, setIsEditingFlag] = useState(false);
  const [rules, setRules] = useState([]);

  // cache of offerings keyed by programmePointID
  const [offeringsMap, setOfferingsMap] = useState({});

  // Data hook
  const hostUrl = initialData?.event?.host_url;
  const eventId = initialData?.event?.eventId;
  const {
    programmePoints,
    participantTypes,
    participantStates,
    workGroups,
    fetchProgrammeOfferingsByPP,
  } = useLeadPolicyEditorData(hostUrl, eventId, visible);

  // init edit/create
  useEffect(() => {
    if (!visible) return;

    if (initialData?.name) {
      setIsEditingFlag(true);

      form.setFieldsValue({
        name: initialData.name,
        checkDirection: /checkDirection="yes"/.test(initialData.configuration),
        denySuccessiveIns: /denySuccessiveIns="yes"/.test(initialData.configuration),
      });

      // 1. load rules from the backend
      const apiRules = initialData.rules || [];
      setRules(apiRules);

      // 2. for each different programmePoint, preload your offerings
      (async () => {
        const grouped = {};

        for (const r of apiRules) {
          const ids = [
            r.programmePointBookingID,
            r.paidProgrammePointBookingID,
          ].filter(Boolean);

          for (const ppId of ids) {
            if (!grouped[ppId]) {
              grouped[ppId] = await fetchProgrammeOfferingsByPP(ppId);
            }
          }
        }

        setOfferingsMap(grouped);
      })();

    } else {
      setIsEditingFlag(false);
      form.resetFields();
      setRules([]);
      setOfferingsMap({});
    }
  }, [visible, initialData, form]);

  // rule helpers
  const addRule = () => setRules([...rules, { ...RULE_TEMPLATE }]);
  const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));
  const moveRule = (i, dir) => {
    if ((dir === -1 && i === 0) || (dir === 1 && i === rules.length - 1)) return;
    const copy = [...rules];
    const [item] = copy.splice(i, 1);
    copy.splice(i + dir, 0, item);
    setRules(copy);
  };
  const updateRule = (i, patch) => setRules(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const handleProgrammePointChange = async (idx, ppId) => {
    updateRule(idx, { programmePointBookingID: ppId, programmeOfferingBookingID: null });
    if (!ppId) return;
    if (!offeringsMap[ppId]) {
      const offs = await fetchProgrammeOfferingsByPP(ppId);
      setOfferingsMap((prev) => ({ ...prev, [ppId]: offs }));
    }
  };

  // XML builder
  const buildXml = (values) => {
    const root = `checkDirection="${values.checkDirection ? "yes" : "no"}" denySuccessiveIns="${values.denySuccessiveIns ? "yes" : "no"}"`;
    let xml = `<leadPolicy ${root}>\n`;

    rules.forEach((rule, idx) => {
      xml += `  <!-- Rule #${idx + 1} -->\n`;      // numbered comment

      const attrs = [];
      Object.entries(rule).forEach(([k, v]) => {
        if (v === null || v === undefined || v === false) return;
        let rendered = v;
        if (k === "startTime" || k === "endTime") rendered = formatDateTime(v);
        else if (v === true) rendered = "yes";
        attrs.push(`${k}="${xmlEscape(rendered)}"`);
      });
      xml += `  <rule ${attrs.join(" ")} />\n`;
    });
    xml += "</leadPolicy>";
    return xml;
  };

  // submit
  const handleFinish = async (values) => {
    if (rules.length === 0) {
      message.warning("You must add at least one rule");
      return;
    }

    setLoading(true);
    try {
      const configuration = buildXml(values);

      const payload = {
        eventId: initialData?.event?._id,
        name: values.name || "New Lead Policy",
        configuration,
        rules,
      };

      /*──────────────── choose endpoint ────────────────*/
      const baseUrl = adminMode ? "/lead-policy/admin" : "/lead-policy";
      const url     = isEditing ? `${baseUrl}/${initialData._id}` : baseUrl;

      isEditing
          ? await axios.put(url, payload)
          : await axios.post(url, payload);

      message.success("Saved correctly");
      fetchLeadPolicies();
      onClose();
    } catch (err) {
      console.error(err);
      message.error("Error saving policy");
    } finally {
      setLoading(false);
    }
  };

  // ------- copy to clipboard -------- //
  const handleCopyXml = () => {
    try {
      const xml = buildXml(form.getFieldsValue());
      navigator.clipboard.writeText(xml);
      message.success("XML copied to clipboard");
    } catch {
      message.error("Error generating XML");
    }
  };

  // Rule card UIofferingsForPP
  const renderRuleCard = (rule, idx) => {
    const offeringsForPP = rule.programmePointBookingID ? offeringsMap[rule.programmePointBookingID] || [] : [];
    return (
        <Card
            key={idx} size="small"
            style={{ marginBottom: 8 }}
            title={`Rule #${idx + 1}`} extra={
              <Space>
                <Button icon={<UpOutlined />} size="small" onClick={() => moveRule(idx, -1)} />
                <Button icon={<DownOutlined />} size="small" onClick={() => moveRule(idx, 1)} />
                <Popconfirm title="Delete rule?" onConfirm={() => removeRule(idx)}>
                  <Button icon={<DeleteOutlined />} danger size="small" />
                </Popconfirm>
              </Space>
            }
        >
          {/* Row 1 */}
          <Row gutter={8}>
            <Col span={4}>
              <Select
                  value={rule.type}
                  onChange={(v) => updateRule(idx, { type: v })}
                  style={{ width: "100%" }}
              >
                <Option value="allow">allow</Option>
                <Option value="deny">deny</Option>
                <Option value="continue">continue</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                  placeholder="Badge Type"
                  value={rule.badgeType} onChange={(v) => updateRule(idx, { badgeType: v })}
                  allowClear style={{ width: "100%" }}
              >
                <Option value={0}>Personal (0)</Option>
                <Option value={-1}>Baggage (-1)</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                  placeholder="Participant Type"
                  value={rule.participantTypeID}
                  onChange={(v) => updateRule(idx, { participantTypeID: v })}
                  allowClear showSearch optionFilterProp="children"
                  style={{ width: "100%" }}
              >
                {participantTypes.map((pt) => (
                    <Option key={pt.id} value={pt.id}>{pt.name?.[0]?.value}</Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Select
                  placeholder="Participant State"
                  value={rule.participantStateID}
                  onChange={(v) => updateRule(idx, { participantStateID: v })}
                  allowClear
                  style={{ width: "100%" }}
              >
                {participantStates.map((ps) => (
                    <Option key={ps.id} value={ps.id}>{ps.name?.[0]?.value}</Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <DatePicker
                  format="DD.MM.YYYY HH:mm"
                  showTime={{ format: "HH:mm", showSecond: false }}
                  placeholder="Start" value={rule.startTime}
                  onChange={(v) => updateRule(idx, { startTime: v })}
                  style={{ width: "100%" }} />
            </Col>
            <Col span={4}>
              <DatePicker
                  format="DD.MM.YYYY HH:mm"
                  showTime={{ format: "HH:mm",
                  showSecond: false }}
                  placeholder="End"
                  value={rule.endTime} onChange={(v) => updateRule(idx, { endTime: v })}
                  style={{ width: "100%" }} />
            </Col>
          </Row>

          {/* Row 2 */}
          <Row gutter={8} style={{ marginTop: 8 }}>
            <Col span={4}>
              <Switch
                  checked={rule.demandsBooking}
                  onChange={(v) => updateRule(idx, { demandsBooking: v })}
                  checkedChildren="Demands Booking"
                  unCheckedChildren="Demands Booking" />
            </Col>
            <Col span={4}>
              <Switch
                  checked={rule.demandsPaidBooking}
                  onChange={(v) => updateRule(idx, { demandsPaidBooking: v })}
                  checkedChildren="Demands Paid Booking"
                  unCheckedChildren="Demands Paid Booking" />
            </Col>
            <Col span={8}>
              <Select
                  placeholder="Programme Point"
                  value={rule.programmePointBookingID}
                  onChange={(v) => handleProgrammePointChange(idx, v)}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  style={{ width: "100%" }}
              >
                {programmePoints.map((pp) => (
                    <Option key={pp.id} value={pp.id}>{pp.name?.[0]?.value}</Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <Select
                  placeholder="Programme Offering"
                  value={rule.programmeOfferingBookingID}
                  onChange={(v) => updateRule(idx, { programmeOfferingBookingID: v })}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  disabled={!rule.programmePointBookingID}
                  style={{ width: "100%" }}>{offeringsForPP.map((po) => (
                      <Option key={po.id} value={po.id}>{po.PO_Name}</Option>
                  ))}
              </Select>
            </Col>
          </Row>

          {/* Row 3 */}
          <Row gutter={8} style={{ marginTop: 8 }}>
            <Col span={8}>
              <Select
                  placeholder="Paid Programme Point"
                  value={rule.paidProgrammePointBookingID}
                  onChange={(v) => updateRule(idx, { paidProgrammePointBookingID: v })}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  style={{ width: "100%" }}
              >
                {programmePoints.map((pp) => (
                    <Option key={pp.id} value={pp.id}>{pp.name?.[0]?.value}</Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <Select
                  placeholder="Paid Programme Offering"
                  value={rule.paidProgrammeOfferingBookingID}
                  onChange={(v) => updateRule(idx, { paidProgrammeOfferingBookingID: v })}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  disabled={!rule.programmePointBookingID}
                  style={{ width: "100%" }}>{offeringsForPP.map((po) => (
                  <Option key={po.id} value={po.id}>{po.PO_Name}</Option>
              ))}
              </Select>
            </Col>
            <Col span={5}>
              <Select
                  placeholder="workGroupID"
                  value={rule.workGroupID}
                  onChange={(v) => updateRule(idx, { workGroupID: v })}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  style={{ width: "100%" }}>{workGroups.map((wg) => (
                      <Option key={wg.id} value={wg.id}>{wg.name?.[0]?.value}</Option>
                  ))}
              </Select>
            </Col>
            <Col span={3}>
              <InputNumber
                  placeholder="Max. Allow"
                  min={1}
                  value={rule.maxAllow}
                  onChange={(value) => updateRule(idx, { maxAllow: value ?? null })}
                  style={{ width: "100%" }}
              />
            </Col>
          </Row>
        </Card>
    );
  };

  return (
      <Modal title={`Lead Policy Editor: ${initialData?.event?.mnemonic || "New"}`} open={visible} width={1280} style={{ top: 20 }} onCancel={onClose} footer={null} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="Name" />
          </Form.Item>
          <Form.Item
              label="Lead Policy settings">
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                    valuePropName="checked"
                    name="checkDirection"
                    noStyle initialValue={true}>
                  <Switch
                      checkedChildren="checkDirection yes"
                      unCheckedChildren="checkDirection no"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                    valuePropName="checked"
                    name="denySuccessiveIns"
                    noStyle
                    initialValue={true}>
                  <Switch
                      checkedChildren="denySuccessiveIns yes"
                      unCheckedChildren="denySuccessiveIns no"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
          <Form.Item
              label="Rules">
            {rules.map((r, i) => renderRuleCard(r, i))}
            <Button block type="dashed" icon={<PlusOutlined />}
                    onClick={addRule}>Add rule
            </Button>
          </Form.Item>
          <Form.Item shouldUpdate>
            {() => {
              try {
                const xml = buildXml(form.getFieldsValue());
                const name     = form.getFieldValue("name") || "(unnamed)";
                const ruleCnt  = rules.length;
                return (
                  <>
                    <Row justify="space-between" align="middle" style={{ marginBottom: 4 }}>
                       <Col>
                         <Typography.Text strong>
                           <span style={{ color: "#1890ff" }}>{name}</span>
                              {" – "}
                           <span style={{ color: "#52c41a" }}>
                              {`${ruleCnt} rule${ruleCnt !== 1 ? "s" : ""} added`}
                            </span>
                         </Typography.Text>
                       </Col>
                       <Col>
                         <Button
                           size="small"
                           icon={<CopyOutlined />}
                           onClick={handleCopyXml}
                         >
                           Copy XML
                         </Button>
                        </Col>
                    </Row>
                    <Input.TextArea value={xml} rows={8} readOnly />
                  </>
                );
              } catch {
                return null;
              }
            }}
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} style={{ marginRight: 8 }}>Save</Button>
            <Button onClick={onClose}>Cancel</Button></Form.Item>
        </Form>
      </Modal>
  );
};

export default LeadPolicyEditorForm;
